import { useEffect, useState } from 'react';
import { Globe, ImageOff } from 'lucide-react';
import { RemoteImage } from '@/components/remote-image';
import { cn } from '@/lib/utils';
import type { LinkSummary, ScreenshotStatus } from '@/types/api';

const FAILED_SCREENSHOT: readonly ScreenshotStatus[] = ['FAILED', 'BLOCKED'];

function placeholderCaption(status: ScreenshotStatus): string {
  if (status === 'FAILED') return 'Screenshot unavailable';
  if (status === 'BLOCKED') return 'Screenshot blocked';
  if (status === 'PENDING' || status === 'PROCESSING') return 'Screenshot in progress';
  return 'No screenshot';
}

/**
 * Screenshot thumbnail with a designed placeholder. `thumbnailUrl` is a short-lived presigned
 * S3 URL, so an expired link is a normal outcome, not an error — RemoteImage falls back
 * silently. The og:image is used as a second chance before giving up.
 */
export function LinkThumbnail({
  link,
  className,
  imageClassName,
}: {
  link: Pick<LinkSummary, 'thumbnailUrl' | 'ogImageUrl' | 'screenshotStatus' | 'title' | 'domain'>;
  className?: string;
  imageClassName?: string;
}) {
  const src = link.thumbnailUrl ?? link.ogImageUrl;
  const caption = placeholderCaption(link.screenshotStatus);
  // A portrait image (a reel or story cover) is shown whole: cropped into a landscape frame, all
  // that is left of it is its top edge.
  const [portrait, setPortrait] = useState(false);
  useEffect(() => {
    setPortrait(false);
  }, [src]);

  return (
    <RemoteImage
      src={src}
      alt={`Screenshot of ${link.title ?? link.domain}`}
      className={cn(
        'h-full w-full object-cover object-top',
        imageClassName,
        portrait && 'object-contain object-center',
      )}
      onLoad={(image) => setPortrait(image.naturalHeight > image.naturalWidth)}
      wrapperClassName={cn(
        'h-full w-full flex-col gap-1.5 bg-muted text-muted-foreground',
        className,
      )}
      fallback={
        <>
          <ImageOff className="h-6 w-6" aria-hidden="true" />
          <span
            className={cn(
              'px-2 text-center text-[11px] font-medium',
              FAILED_SCREENSHOT.includes(link.screenshotStatus) && 'text-muted-foreground',
            )}
          >
            {caption}
          </span>
        </>
      }
    />
  );
}

/** Site favicon, falling back to a generic globe. Decorative — the domain text carries meaning. */
export function LinkFavicon({
  link,
  className,
}: {
  link: Pick<LinkSummary, 'faviconUrl' | 'domain'>;
  className?: string;
}) {
  return (
    <RemoteImage
      src={link.faviconUrl}
      alt=""
      className={cn('h-4 w-4 shrink-0 rounded-sm object-contain', className)}
      wrapperClassName={cn('h-4 w-4 shrink-0', className)}
      fallback={<Globe className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
    />
  );
}
